import { InputProps, InputRef } from "antd";
import React, { ReactElement, forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { InputWithError } from "../InputWithErrorMsg";
import { filterInputValueForNumber } from "@obsbot/remo-utils";
import { RemoI18nTFunction } from "@obsbot/remo-i18n";
export interface InputWithCodeBaseProp {
    isCodeError: boolean;
    errorMsg?: string;
    code: string;
    setCode: (code: string) => void;
    t: RemoI18nTFunction;
    onFocus?: () => void;
    onGetCode: () => Promise<boolean>;
    alreadySending?: boolean;
}

export type InputWithCodeProp = InputWithCodeBaseProp & InputProps;

export interface InputWithCodeRef extends InputRef {
    reset: () => void;
}

export const InputWithCode = forwardRef<InputWithCodeRef, InputWithCodeProp>(
    (
        {
            isCodeError,
            errorMsg,
            onFocus,
            className,
            t,
            onGetCode,
            code,
            alreadySending = false,
            setCode,
            ...props
        },
        ref,
    ): ReactElement => {
        const handleFocus = (): void => {
            onFocus?.();
        };
        // 倒计时
        const [countdown, setCountdown] = useState(alreadySending ? 60 : 0);
        const [isSending, setIsSending] = useState(alreadySending);
        const inputRef = React.useRef<InputRef | null>(null);

        useImperativeHandle(
            ref,
            () =>
                ({
                    ...inputRef,
                    reset: () => {
                        setCountdown(0);
                        setIsSending(false);
                    },
                }) as unknown as InputWithCodeRef,
        );

        useEffect(() => {
            if (isSending) {
                setCountdown(60);
            }
        }, [isSending]);

        // 更新倒计时
        useEffect(() => {
            if (countdown <= 0) {
                isSending && setIsSending(false);
                return;
            }
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [countdown, isSending]);
        // 获取验证码, 如果已经发送过验证码，则不允许再次发送
        const handleGetCode = async (): Promise<void> => {
            if (countdown > 0 || isSending) {
                return;
            }
            setIsSending(true);
            const res = await onGetCode();
            console.log("调用验证码返回成功或失败", res);
            if (!res) {
                setCountdown(0);
                setIsSending(false);
            }
        };

        return (
            <InputWithError
                ref={inputRef}
                className={`${className} code-modal-input`}
                errorMsg={errorMsg}
                isError={isCodeError}
                maxLength={6}
                suffix={
                    countdown > 0 ? (
                        <span
                            className="code-input-modal-countdown"
                            style={{ cursor: "not-allowed" }}
                        >
                            {t("verifyCodeWait", { seconds: countdown })}
                        </span>
                    ) : (
                        <div
                            className="code-input-modal-get-code"
                            style={{ cursor: isSending ? "not-allowed" : "pointer" }}
                            onClick={handleGetCode}
                        >
                            {t("getVerifyCode")}
                        </div>
                    )
                }
                value={code}
                onChange={e => {
                    setCode(filterInputValueForNumber(e.target.value));
                    handleFocus();
                }}
                onFocus={() => handleFocus()}
                {...props}
            />
        );
    },
);

InputWithCode.displayName = "InputWithCode";
